import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaStar, FaStarHalfAlt, FaGoogle, FaPhone, FaArrowRight } from 'react-icons/fa';
import '../styles/GoogleReviewsSection.css';

// ─────────────────────────────────────────────────────────
// MOCK DATA — structured for easy Google Places API swap
// Replace this array with live API response mapped to the
// same shape: { author_name, rating, relative_time_description,
// text, profile_photo_url? }
// ─────────────────────────────────────────────────────────
const MOCK_REVIEWS = [
  {
    author_name: 'Tammieous Miller',
    rating: 5,
    relative_time_description: '3 weeks ago',
    text: "The owner made it a great experience buying my car. He was very respectful, honest, patient and helpful through the whole process. He made everything easy to understand and never pressured me into anything. You can tell he really cares about his customers and wants to help people get into a good vehicle. I truly appreciate all the help and kindness he showed me. If you're looking for someone trustworthy and professional, I definitely recommend him!",
    profile_photo_url: null,
  },
  {
    author_name: 'Chase Lampley',
    rating: 5,
    relative_time_description: 'a month ago',
    text: "Wayne and Colton are amazing. They are my go to mechanic. I was having an issue with my 80 series Land Cruiser that nobody could seem to figure out, but they did! I've always worked on this car myself, but was truly stumped. I took it to another mechanic locally that has done a lot of work for me and always does a great job, but they could not figure it out either. Finally got it to Wayne and Colton. They were patient and thorough and solved the problem that none of us could figure out. Plus, they are genuine, good Christian dudes. I highly recommend these guys.",
    profile_photo_url: null,
  },
  {
    author_name: 'Ethan Caldwell',
    rating: 5,
    relative_time_description: '2 months ago',
    text: "Amazing experience with Paradise Performance! They got everything done super fast and kept me in the loop the whole time. Great communication and super easy to work with — definitely coming back! Thanks Wayne and Colton!!",
    profile_photo_url: null,
  },
  {
    author_name: 'James Gray',
    rating: 5,
    relative_time_description: '2 months ago',
    text: "I was thoroughly impressed with Paradise Performance. They fixed a complex HVAC module issue on my Audi Q7 using Odis — a task many shops shy away from. The guys seem highly intelligent, articulate and transparent about the repair process.",
    profile_photo_url: null,
  },
  {
    author_name: 'V Chavez',
    rating: 5,
    relative_time_description: '2 months ago',
    text: "Great price. Brought my truck in for a simple oil change. Colton and Wayne took care of it for me with fast, friendly and high end service. Definitely recommend them for any type of automotive service you need.",
    profile_photo_url: null,
  },
  {
    author_name: 'Kyle Boen',
    rating: 5,
    relative_time_description: '3 months ago',
    text: "I bought a car from the team at Paradise Performance and would highly recommend if you are in the market for a used car. Wayne and Colton, being mechanics themselves, gave me a great sense of peace knowing that the car that I was buying from them was mechanically sound. Will always start here first for any future car purchases.",
    profile_photo_url: null,
  },
];

// Aggregate stats — update when pulling from API
const GOOGLE_STATS = {
  averageRating: 4.9,
  totalReviews: 52,
  googleMapsUrl: 'https://www.google.com/maps/place/Paradise+Performance',
  reviewUrl: 'https://share.google/FlmJBuAlMR2o8YWQB',
};

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────
function getInitials(name) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #1e3a5f, #2d5f8a)',
  'linear-gradient(135deg, #3b1f2b, #7a3b52)',
  'linear-gradient(135deg, #1a3c34, #2d6a5a)',
  'linear-gradient(135deg, #3d2c1e, #7a5a3e)',
  'linear-gradient(135deg, #2a1f3d, #5a3d7a)',
  'linear-gradient(135deg, #1f2d3d, #3d5a7a)',
];

function getAvatarGradient(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

// ─────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────
function StarRating({ rating, size = 16, className = '' }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (i <= Math.floor(rating)) {
      stars.push(<FaStar key={i} size={size} className="text-amber-400" />);
    } else if (i - 0.5 <= rating) {
      stars.push(<FaStarHalfAlt key={i} size={size} className="text-amber-400" />);
    } else {
      stars.push(<FaStar key={i} size={size} className="text-gray-300" />);
    }
  }
  return <div className={`pp-star-row ${className}`}>{stars}</div>;
}

function ReviewCard({ review, index }) {
  return (
    <motion.article
      className="pp-review-card p-6"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        duration: 0.5,
        delay: index * 0.1,
        ease: [0.23, 1, 0.32, 1],
      }}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        {/* Avatar */}
        {review.profile_photo_url ? (
          <img
            src={review.profile_photo_url}
            alt={review.author_name}
            className="w-11 h-11 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div
            className="pp-avatar-initials"
            style={{ background: getAvatarGradient(review.author_name) }}
          >
            {getInitials(review.author_name)}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h4 className="text-[#1a1a1a] font-semibold text-[15px] leading-tight">
            {review.author_name}
          </h4>
          <div className="flex items-center gap-2 mt-1">
            <StarRating rating={review.rating} size={14} />
            <span className="text-[#999] text-xs">{review.relative_time_description}</span>
          </div>
        </div>

        {/* Google icon */}
        <FaGoogle className="text-[#4285F4] text-sm flex-shrink-0 mt-1 opacity-60" />
      </div>

      {/* Review body */}
      <p className="pp-review-text text-[#444] text-[14.5px] leading-[1.65]">
        "{review.text}"
      </p>
    </motion.article>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16">
      <FaGoogle className="text-4xl text-gray-300 mx-auto mb-4" />
      <p className="text-gray-500 text-lg font-medium">Reviews are loading.</p>
      <p className="text-gray-400 text-sm mt-1">Check back shortly for the latest feedback.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────
export default function GoogleReviewsSection({
  reviews: reviewsProp,
  stats: statsProp,
  maxVisible = 4,
}) {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' });

  // Live data from the Netlify function, falling back to mock data.
  // If a caller passes explicit props, those win and we skip fetching.
  const [reviews, setReviews] = useState(reviewsProp ?? MOCK_REVIEWS);
  const [stats, setStats] = useState(statsProp ?? GOOGLE_STATS);

  useEffect(() => {
    if (reviewsProp || statsProp) return; // explicit override — don't fetch

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch('/api/google-reviews.php');
        if (!res.ok) return; // keep mock fallback
        const data = await res.json();
        if (cancelled) return;

        if (Array.isArray(data.reviews) && data.reviews.length > 0) {
          setReviews(data.reviews);
        }
        if (data.stats) {
          setStats((prev) => ({
            ...prev,
            averageRating: data.stats.averageRating ?? prev.averageRating,
            totalReviews: data.stats.totalReviews ?? prev.totalReviews,
          }));
        }
      } catch {
        // Network/API failure — silently keep the mock fallback.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reviewsProp, statsProp]);

  const visibleReviews = reviews.slice(0, maxVisible);

  return (
    <section
      ref={sectionRef}
      id="pp-google-reviews"
      className="pp-google-reviews-section py-20 md:py-28 lg:py-32"
      aria-label="Google customer reviews"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* ─── Intro Row ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
          {/* Left: Headline block */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            {/* Eyebrow */}
            <div className="flex items-center gap-2 mb-4">
              <span className="pp-trust-dot" />
              <span
                className="text-xs font-semibold uppercase tracking-[0.15em]"
                style={{ color: '#888' }}
              >
                Verified on Google
              </span>
            </div>

            <h2
              className="text-4xl md:text-5xl lg:text-[3.25rem] font-bold leading-[1.1] tracking-tight mb-5"
              style={{ color: '#111' }}
            >
              Trusted by Drivers
              <br />
              <span style={{ color: '#666' }}>Across North Texas</span>
            </h2>

            <p
              className="text-base md:text-[17px] leading-relaxed max-w-md"
              style={{ color: '#666' }}
            >
              Real feedback from drivers who trusted Paradise Performance with their European cars, truck purchases and everything in between.
            </p>

            {/* Summary card */}
            <motion.div
              className="pp-summary-card mt-8 p-5 flex items-center gap-5"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="flex flex-col items-center gap-1">
                <span
                  className="text-3xl font-bold"
                  style={{ color: '#111' }}
                >
                  {stats.averageRating}
                </span>
                <StarRating rating={stats.averageRating} size={16} />
              </div>

              <div className="w-px h-12 bg-black/[0.08]" />

              <div className="flex items-center gap-3">
                <FaGoogle className="text-xl" style={{ color: '#4285F4' }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#222' }}>
                    Google Reviews
                  </p>
                  <p className="text-base font-semibold" style={{ color: '#555' }}>
                    {stats.totalReviews} verified reviews
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Review grid */}
          <div className="lg:col-span-7">
            {visibleReviews.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {visibleReviews.map((review, i) => (
                  <ReviewCard
                    key={review.author_name + i}
                    review={review}
                    index={i}
                  />
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </div>

        {/* ─── CTA Row ─── */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <a
            href={stats.reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pp-cta-primary"
          >
            <FaGoogle />
            Read Our Google Reviews
            <FaArrowRight size={13} />
          </a>

          <a href="tel:+18173822316" className="pp-cta-secondary">
            <FaPhone size={13} />
            Call Paradise Performance
          </a>
        </motion.div>

        {/* ─── Trust Line ─── */}
        <motion.p
          className="text-center mt-8 text-xs tracking-wide"
          style={{ color: '#aaa' }}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          Reviews sourced directly from Google Business Profile • Updated regularly
        </motion.p>
      </div>

      {/* ─── AggregateRating Schema ─── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AutoRepair',
            name: 'Paradise Performance',
            telephone: '+1-817-382-2316',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Paradise',
              addressRegion: 'TX',
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: stats.averageRating.toString(),
              reviewCount: stats.totalReviews.toString(),
              bestRating: '5',
              worstRating: '1',
            },
          }),
        }}
      />
    </section>
  );
}
